/**
 * Select-inside-text → color (Studio Phase 5 gold + Phase 3.1 arbitrary
 * color).
 *
 * The cue editor works in OPERATOR syntax (see emToSlashes/slashesToEm in
 * clipEditState.ts): `/…/` = the gold accent span, `/…/{#RRGGBB}` = an
 * operator-colored span. This module implements "apply a color to a textarea
 * selection", entirely in that display syntax:
 *
 *   - the selection is NORMALIZED: whitespace trimmed, then expanded outward
 *     to whole words (a half-selected word colors fully);
 *   - if every selected word already has the SAME target color → UNWRAP
 *     (toggle off) exactly the selection (a larger span splits around it);
 *   - otherwise → WRAP the selection in the target color; adjacent spans of
 *     the SAME color separated only by spaces MERGE into one span;
 *   - spans never cross a line break and never start/end on whitespace.
 *
 * Per-char color model: `null` = plain, `ACCENT` (empty string) = the gold
 * default (`<em>`), `"#RRGGBB"` = an explicit color (`<em c="…">`). Gold is
 * just the `ACCENT` case, so `toggleGoldSelection` is `applyColorSelection`
 * with target `ACCENT` — one code path, byte-identical gold behavior.
 *
 * Pure string→string logic (no DOM) so it unit-tests under root vitest and
 * the web layer just feeds it textarea.selectionStart/End.
 */

/** The gold-accent target: a `<em>` span with no explicit color. */
export const ACCENT = "";
/** A per-char color: null = plain, "" = gold accent, "#hex" = explicit. */
type SpanColor = string | null;

interface CharModel {
  /** Display text with the slash/color markers removed. */
  visible: string;
  /** Per-visible-char color (null = plain). */
  color: SpanColor[];
  /** visForDisp[d] = visible index at (or after) display index d.
   *  Length display.length + 1. */
  visForDisp: number[];
}

/** A span in one line: `/…/` (gold) or `/…/{#hex}` (colored). Group 1 = text,
 *  group 2 = optional #hex. */
const SPAN_RE = /\/([^/\n]+)\/(?:\{(#[0-9a-fA-F]{6})\})?/g;

function parseDisplay(display: string): CharModel {
  const chars: string[] = [];
  const color: SpanColor[] = [];
  const visForDisp: number[] = new Array(display.length + 1).fill(0);
  let d = 0;
  const lines = display.split("\n");
  lines.forEach((line, li) => {
    const spans: Array<{ start: number; end: number; color: string }> = [];
    let m: RegExpExecArray | null;
    SPAN_RE.lastIndex = 0;
    while ((m = SPAN_RE.exec(line)) !== null) {
      spans.push({ start: m.index, end: m.index + m[0].length, color: m[2] ?? ACCENT });
    }
    let i = 0;
    let spanIdx = 0;
    while (i < line.length) {
      const span = spans[spanIdx];
      if (span && i === span.start) {
        // Opening slash.
        visForDisp[d + i] = chars.length;
        i++;
        // Inner text runs until the closing slash (which sits before an
        // optional "{#hex}" suffix). Find the closing slash position.
        const closeSlash = line.indexOf("/", i);
        while (i < closeSlash) {
          visForDisp[d + i] = chars.length;
          chars.push(line[i]!);
          color.push(span.color);
          i++;
        }
        // Consume the closing slash + any "{#hex}" suffix (marker chars).
        while (i < span.end) {
          visForDisp[d + i] = chars.length;
          i++;
        }
        spanIdx++;
      } else {
        visForDisp[d + i] = chars.length;
        chars.push(line[i]!);
        color.push(null);
        i++;
      }
    }
    d += line.length;
    if (li < lines.length - 1) {
      visForDisp[d] = chars.length;
      chars.push("\n");
      color.push(null);
      d += 1;
    }
  });
  visForDisp[display.length] = chars.length;
  return { visible: chars.join(""), color, visForDisp };
}

/** Emit display syntax from visible chars + per-char colors. Spans are
 *  snapped off edge whitespace and never carry a newline. */
function serialize(visible: string, colorFlags: SpanColor[]): string {
  const colors = [...colorFlags];
  for (let i = 0; i < visible.length; i++) if (visible[i] === "\n") colors[i] = null;
  // Shrink every colored run over its leading/trailing whitespace.
  let i = 0;
  while (i < visible.length) {
    if (colors[i] === null) {
      i++;
      continue;
    }
    const runColor = colors[i];
    let j = i;
    while (j < visible.length && colors[j] === runColor) j++;
    let s = i;
    let e = j;
    while (s < e && /\s/.test(visible[s]!)) colors[s++] = null;
    while (e > s && /\s/.test(visible[e - 1]!)) colors[--e] = null;
    i = j;
  }
  const wrap = (c: SpanColor): string => (c === ACCENT ? "/" : c === null ? "" : "/");
  const suffix = (c: SpanColor): string => (c && c !== ACCENT ? `{${c}}` : "");
  let out = "";
  let cur: SpanColor = null;
  for (let k = 0; k < visible.length; k++) {
    const c = colors[k]!;
    if (c !== cur) {
      if (cur !== null) out += "/" + suffix(cur);
      if (c !== null) out += wrap(c);
      cur = c;
    }
    out += visible[k];
  }
  if (cur !== null) out += "/" + suffix(cur);
  return out;
}

export interface GoldToggleResult {
  /** The new display text (operator syntax). */
  text: string;
  /** New selection range (display coords) covering the toggled words. */
  selStart: number;
  selEnd: number;
  /** False when the toggle was a no-op (whitespace-only selection…). */
  changed: boolean;
  /** True when the action WRAPPED (the selection carries the color afterwards). */
  nowGold: boolean;
}

/**
 * Apply `target` color to the [selStart, selEnd) selection of `display`
 * (operator syntax). Empty selection = the word under the caret. Re-applying
 * the SAME color to an all-that-color selection toggles it OFF.
 */
export function applyColorSelection(
  display: string,
  selStart: number,
  selEnd: number,
  target: SpanColor,
): GoldToggleResult {
  const noop: GoldToggleResult = { text: display, selStart, selEnd, changed: false, nowGold: false };
  const a = Math.max(0, Math.min(display.length, Math.min(selStart, selEnd)));
  const b = Math.max(0, Math.min(display.length, Math.max(selStart, selEnd)));

  const model = parseDisplay(display);
  const V = model.visible;
  let vs = model.visForDisp[a]!;
  let ve = model.visForDisp[b]!;

  // Trim whitespace inside the selection.
  while (vs < ve && /\s/.test(V[vs]!)) vs++;
  while (ve > vs && /\s/.test(V[ve - 1]!)) ve--;

  // Collapsed CARET → the word under it. A non-empty selection that was only
  // whitespace stays a no-op (coloring a neighbour would surprise).
  if (vs >= ve) {
    if (a !== b) return noop;
    const p = model.visForDisp[a]!;
    if (p < V.length && /\S/.test(V[p]!)) {
      vs = p;
      ve = p + 1;
    } else if (p > 0 && /\S/.test(V[p - 1]!)) {
      vs = p - 1;
      ve = p;
    } else {
      return noop;
    }
  }

  // Expand outward to whole words.
  while (vs > 0 && /\S/.test(V[vs - 1]!) && /\S/.test(V[vs]!)) vs--;
  while (ve < V.length && /\S/.test(V[ve]!) && /\S/.test(V[ve - 1]!)) ve++;

  let hasNonSpace = false;
  let allTarget = true;
  for (let k = vs; k < ve; k++) {
    if (/\S/.test(V[k]!)) {
      hasNonSpace = true;
      if (model.color[k] !== target) allTarget = false;
    }
  }
  if (!hasNonSpace) return noop;

  const flags = [...model.color];
  if (allTarget) {
    // Toggle OFF — the whole selection already carries the target color.
    for (let k = vs; k < ve; k++) flags[k] = null;
  } else {
    for (let k = vs; k < ve; k++) if (V[k] !== "\n") flags[k] = target;
    // Merge adjacent SAME-color spans: an intra-line whitespace run between
    // two target-colored chars turns target too, so "/a/ /b/" → "/a b/".
    let k = 0;
    while (k < V.length) {
      if (/\s/.test(V[k]!) && V[k] !== "\n") {
        let j = k;
        while (j < V.length && /\s/.test(V[j]!) && V[j] !== "\n") j++;
        const leftMatch = k > 0 && flags[k - 1] === target && V[k - 1] !== "\n";
        const rightMatch = j < V.length && flags[j] === target;
        if (leftMatch && rightMatch) for (let t = k; t < j; t++) flags[t] = target;
        k = j;
      } else {
        k++;
      }
    }
  }

  const text = serialize(V, flags);
  if (text === display) return noop;

  // Map the toggled visible range back into the NEW display string.
  const after = parseDisplay(text);
  const dispForVis: number[] = new Array(after.visible.length + 1).fill(-1);
  for (let dd = 0; dd < text.length; dd++) {
    const v0 = after.visForDisp[dd]!;
    const v1 = after.visForDisp[dd + 1]!;
    if (v1 > v0 && dispForVis[v0] === -1) dispForVis[v0] = dd;
  }
  dispForVis[after.visible.length] = text.length;
  const ns = dispForVis[vs] !== -1 ? dispForVis[vs]! : text.length;
  const lastCharDisp = dispForVis[ve - 1] !== -1 ? dispForVis[ve - 1]! : text.length - 1;
  return { text, selStart: ns, selEnd: lastCharDisp + 1, changed: true, nowGold: !allTarget };
}

/**
 * Toggle gold on the [selStart, selEnd) selection — the ACCENT case of
 * applyColorSelection (byte-identical to the Phase-5 behavior).
 */
export function toggleGoldSelection(
  display: string,
  selStart: number,
  selEnd: number,
): GoldToggleResult {
  return applyColorSelection(display, selStart, selEnd, ACCENT);
}

export interface VisibleColorResult {
  /** New display text (operator syntax) to feed slashesToEm / re-render. */
  text: string;
  /** Toggled selection in the NEW text's VISIBLE coordinates. */
  visStart: number;
  visEnd: number;
  changed: boolean;
  /** True when the selection now carries a color (wrap); false for clear/off. */
  nowColored: boolean;
}

/** Smallest display index that maps to each visible index (inverse of
 *  `visForDisp`). Length = visible.length + 1; the final slot maps to the
 *  end of the display string. */
function firstDispForVis(model: CharModel, displayLen: number): number[] {
  const out = new Array(model.visible.length + 1).fill(-1);
  for (let d = 0; d <= displayLen; d++) {
    const v = model.visForDisp[d]!;
    if (out[v] === -1) out[v] = d;
  }
  if (out[model.visible.length] === -1) out[model.visible.length] = displayLen;
  return out;
}

/**
 * WYSIWYG entry point: the operator selects inside the VISIBLE (markup-free)
 * text, so `visStart/visEnd` index that clean text (newlines count as one
 * visible char). We map those to display offsets, delegate to
 * `applyColorSelection` (reusing all its word-snap / merge / toggle logic),
 * then map the toggled range back into the NEW text's visible coordinates so
 * the editor can restore the selection over the just-colored words.
 *
 * `target`: `ACCENT` = gold, `"#hex"` = an explicit color, `null` = clear to
 * plain (the "White" eraser). Clearing an already-plain selection is a no-op.
 */
export function applyColorByVisible(
  display: string,
  visStart: number,
  visEnd: number,
  target: SpanColor,
): VisibleColorResult {
  const before = parseDisplay(display);
  const vlen = before.visible.length;
  const dispForVis = firstDispForVis(before, display.length);
  const vs = Math.max(0, Math.min(vlen, Math.min(visStart, visEnd)));
  const ve = Math.max(0, Math.min(vlen, Math.max(visStart, visEnd)));
  const aDisp = dispForVis[vs] ?? display.length;
  const bDisp = dispForVis[ve] ?? display.length;

  const r = applyColorSelection(display, aDisp, bDisp, target);
  if (!r.changed) {
    return { text: display, visStart: vs, visEnd: ve, changed: false, nowColored: false };
  }

  const after = parseDisplay(r.text);
  const nvs = after.visForDisp[r.selStart] ?? 0;
  const nve = after.visForDisp[r.selEnd] ?? after.visible.length;
  return { text: r.text, visStart: nvs, visEnd: nve, changed: true, nowColored: target !== null && r.nowGold };
}
